// Реестр обучающих туров по страницам админки. Каждый тур — это короткая
// последовательность шагов, каждый шаг подсвечивает элемент на странице
// (target — CSS-селектор, обычно [data-tour="…"]) и показывает подсказку.
//
// Тексты хранятся ключами i18n и резолвятся в момент показа через t(), поэтому
// сам реестр остаётся данными без завязки на язык. Ключи лежат под "tour.pages".
//
// Тур привязан к маршруту предикатом match(pathname). Первый подходящий тур —
// тур страницы; остальные подходящие (например, «Бухгалтерия» на «Финансах»,
// «Перевозчики» на «Доставке») показываются в поповере «?» отдельными кнопками:
// у крупного раздела свой короткий тур, а не один на 12 шагов.

export type TourStep = {
  /** CSS-селектор подсвечиваемого элемента. */
  target: string
  titleKey: string
  textKey: string
}

export type TourDef = {
  /** Стабильный идентификатор — ключ для localStorage «просмотрено». */
  key: string
  match: (pathname: string) => boolean
  titleKey: string
  descKey: string
  steps: TourStep[]
}

const exact = (path: string) => (pathname: string) => pathname === path

const currentTab = (): string | null => {
  try {
    return new URLSearchParams(window.location.search).get("tab")
  } catch {
    return null
  }
}

// Шаг по соглашению: [data-tour="<prefix>-<id>"], тексты под tour.pages.<page>.steps.<id>.
const steps = (page: string, prefix: string, ids: string[]): TourStep[] =>
  ids.map((id) => ({
    target: `[data-tour="${prefix}-${id}"]`,
    titleKey: `tour.pages.${page}.steps.${id}.title`,
    textKey: `tour.pages.${page}.steps.${id}.text`,
  }))

const tour = (
  key: string,
  match: (pathname: string) => boolean,
  page: string,
  prefix: string,
  ids: string[],
): TourDef => ({
  key,
  match,
  titleKey: `tour.pages.${page}.title`,
  descKey: `tour.pages.${page}.desc`,
  steps: steps(page, prefix, ids),
})

export const TOURS: TourDef[] = [
  tour("dashboard", exact("/dashboard"), "dashboard", "dashboard", [
    "intro",
    "controls",
    "audience",
    "metrics",
  ]),
  tour("applications", exact("/applications"), "applications", "applications", [
    "intro",
    "funnel",
    "alerts",
    "view",
  ]),
  tour("products", exact("/products"), "products", "products", [
    "status",
    "filters",
    "price-requests",
    "table",
  ]),
  tour("feed", exact("/feed"), "feed", "feed", ["intro", "segments", "create", "list"]),
  tour("ideas", exact("/ideas"), "ideas", "ideas", [
    "intro",
    "tabs",
    "create",
    "filters",
    "status",
    "metrics",
  ]),
  tour(
    "idea-editor",
    (pathname) => pathname === "/ideas/new" || /^\/ideas\/[^/]+\/edit\/?$/.test(pathname),
    "ideaEditor",
    "idea-editor",
    ["intro", "photo", "title", "audience", "products", "actions"],
  ),
  tour("notification-campaigns", exact("/notification-campaigns"), "campaigns", "campaigns", [
    "intro",
    "filter",
    "create",
    "table",
  ]),
  tour("finances", exact("/finances"), "finances", "finances", ["intro", "contour", "range"]),
  tour("finances-accounting", exact("/finances"), "accounting", "finances", [
    "payouts",
    "registry",
    "cashflow",
  ]),
  tour("commissions", exact("/commissions"), "commissions", "commissions", [
    "intro",
    "period",
    "base",
    "categories",
    "shops",
    "history",
  ]),
  tour("delivery", exact("/delivery"), "delivery", "delivery", ["intro", "fees", "free", "save"]),
  tour("delivery-carriers", exact("/delivery"), "carriers", "carriers", [
    "intro",
    "list",
    "toggle",
    "zones",
    "check",
  ]),
  tour("support", exact("/support"), "support", "support", ["intro", "tabs", "filters"]),
  // Разделы администрирования консоли (26.09.2026): до этого «?» на них
  // показывал только общую подсказку.
  tour("integrations", exact("/integrations"), "integrations", "integrations", [
    "intro",
    "payments",
    "cards",
    "costs",
    "events",
  ]),
  tour("reports", exact("/reports"), "reports", "reports", ["intro", "period", "catalog", "history"]),
  tour("audit", exact("/audit"), "audit", "audit", ["intro", "filters", "table"]),
  tour("platform-roles", exact("/platform-roles"), "roles", "roles", [
    "intro",
    "list",
    "permissions",
    "members",
  ]),
  tour("email", exact("/email"), "email", "email", ["intro", "status", "test", "templates", "log"]),
  // Цифровые UGC-артикулы (31.10.2026). Вкладка живёт на /promos?tab=ugc:
  // предикат получает только путь, вкладку читаем из адреса - «?»
  // перерисовывается на каждую смену адреса, включая ?tab=.
  tour("promos-ugc", (pathname) => pathname === "/promos" && currentTab() === "ugc", "ugc", "ugc", [
    "summary",
    "create",
    "filters",
    "table",
  ]),
  tour("promos-ugc-card", (pathname) => /^\/promos\/ugc\/[^/]+\/?$/.test(pathname), "ugcCard", "ugc-card", [
    "actions",
    "orders",
    "events",
  ]),
]

/** Все туры страницы: первый — основной, остальные — туры её разделов. */
export const toursForPath = (pathname: string): TourDef[] =>
  TOURS.filter((item) => item.match(pathname))

export const tourForPath = (pathname: string): TourDef | undefined => toursForPath(pathname)[0]
